import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * useBiometric
 *
 * Manages fingerprint device state and scanning flow.
 *
 * deviceStatus values:
 *   'loading'         — fetching config from server
 *   'not_configured'  — feature disabled or no device set up (check Settings)
 *   'ready'           — device enabled and config exists (hardware stub active)
 *   'scanning'        — a scan is in progress
 *   'error'           — last scan or connection failed
 *
 * NOTE: The actual hardware capture happens server-side via BiometricService.
 * Until a real SDK is connected, scan() will always return an error from the
 * backend with code DEVICE_NOT_CONNECTED.
 *
 * For testing the UI flow without hardware, pass simulateMode=true.
 * In simulate mode, scan() resolves after 2 seconds with a fake template.
 */
export default function useBiometric({ simulateMode = false } = {}) {
  const [deviceStatus, setDeviceStatus] = useState('loading');
  const [config, setConfig] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      const response = await api.get('/biometric/device-config');
      const cfg = response?.data ?? response;
      setConfig(cfg);
      setDeviceStatus(cfg?.isEnabled ? 'ready' : 'not_configured');
    } catch (err) {
      setDeviceStatus('not_configured');
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  /**
   * Initiates a fingerprint scan.
   *
   * In REAL mode:
   *   The caller is responsible for collecting templateData from the device
   *   (e.g. via a local WebSocket bridge or device SDK) and passing it here.
   *   If templateData is omitted, the backend will throw DEVICE_NOT_CONNECTED.
   *
   * In SIMULATE mode:
   *   Returns a fake template after a 2-second delay for UI development.
   *
   * @param {string|null} templateData  Base64 template from the device SDK
   * @returns {{ templateData: string, quality: number }|null}
   */
  const scan = useCallback(async (templateData = null) => {
    setScanning(true);
    setError(null);
    setLastResult(null);

    try {
      if (simulateMode) {
        await new Promise((res) => setTimeout(res, 2000));
        const fakeResult = { templateData: 'SIMULATED_TEMPLATE_BASE64', quality: 85 };
        setLastResult(fakeResult);
        setDeviceStatus('ready');
        return fakeResult;
      }

      if (!templateData) {
        // When hardware is integrated, this should be replaced with:
        // const captureResult = await deviceSdk.capture(); templateData = captureResult.template;
        throw new Error('DEVICE_NOT_CONNECTED: No template data provided. Connect the fingerprint reader and implement the SDK capture.');
      }

      const result = { templateData, quality: 0 };
      setLastResult(result);
      setDeviceStatus('ready');
      return result;
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Scan failed';
      setError(msg);
      setDeviceStatus('error');
      return null;
    } finally {
      setScanning(false);
    }
  }, [simulateMode]);

  const testConnection = useCallback(async () => {
    setError(null);
    try {
      const response = await api.post('/biometric/device-config/test');
      return response?.data ?? response;
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Connection test failed';
      setError(msg);
      return { success: false, message: msg };
    }
  }, []);

  const refreshConfig = useCallback(() => {
    setDeviceStatus('loading');
    return loadConfig();
  }, [loadConfig]);

  return {
    deviceStatus,
    config,
    scanning,
    lastResult,
    error,
    scan,
    testConnection,
    refreshConfig,
  };
}
